import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  TableCell,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../config/api';
import DataTable from '../components/DataTable';

function ProfessionalSpecializations() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/categories');
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Error fetching categories');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (category = null) => {
    if (category) {
      setEditing(category);
      setFormData({
        name: category.name || '',
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
      });
    }
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setFormData({
      name: '',
    });
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      
      // Check for duplicate names (case-insensitive)
      const trimmedName = formData.name.trim();
      const duplicate = categories.find(
        (cat) => 
          cat.name.toLowerCase() === trimmedName.toLowerCase() && 
          (!editing || cat.id !== editing.id)
      );
      
      if (duplicate) {
        setError('A category with this name already exists');
        return;
      }

      if (editing) {
        await api.put(`/categories/${editing.id}`, { name: trimmedName });
      } else {
        await api.post('/categories', { name: trimmedName });
      }
      handleClose();
      fetchCategories();
    } catch (error) {
      setError(error.response?.data?.error || error.response?.data?.message || 'Error saving category');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category? This will also delete all associated subcategories.')) {
      try {
        await api.delete(`/categories/${id}`);
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        alert(error.response?.data?.error || error.response?.data?.message || 'Error deleting category');
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Professional Specializations</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Category
        </Button>
      </Box>

      <DataTable
        data={categories}
        loading={loading}
        searchFields={['name']}
        emptyMessage="No categories found"
        columns={[
          { field: 'id', label: 'ID' },
          { field: 'name', label: 'Name' },
          { field: 'subcategories_count', label: 'Subcategories Count' },
          { field: 'actions', label: 'Actions' },
        ]}
        renderRow={(category) => (
          <TableRow key={category.id}>
            <TableCell>{category.id}</TableCell>
            <TableCell>{category.name}</TableCell>
            <TableCell>{category.subcategories?.length || 0}</TableCell>
            <TableCell>
              <IconButton size="small" onClick={() => handleOpen(category)}>
                <EditIcon />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(category.id)}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        )}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label="Category Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.name.trim()}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProfessionalSpecializations;

